import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../lib/supabase';

function slugify(text) {
  return text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// ── Client form ──────────────────────────────────────────────────────────────
function ClientForm({ client, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name: client?.name || '',
    slug: client?.slug || '',
    delivery_address: client?.delivery_address || '',
    delivery_city: client?.delivery_city || '',
    delivery_zip: client?.delivery_zip || '',
    min_order_bowls: client?.min_order_bowls ?? 1,
    active: client?.active ?? true,
  });
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(prev => {
      const next = { ...prev, [key]: val };
      if (key === 'name' && !client) next.slug = slugify(val);
      return next;
    });
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Company name is required';
    if (!form.slug.trim()) e.slug = 'Slug is required';
    if (!/^[a-z0-9-]+$/.test(form.slug)) e.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    if (!form.delivery_address.trim()) e.delivery_address = 'Delivery address is required';
    if (!form.delivery_city.trim()) e.delivery_city = 'City is required';
    if (!form.delivery_zip.trim()) e.delivery_zip = 'ZIP is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSave({ ...form, min_order_bowls: Number(form.min_order_bowls) });
  };

  const field = (label, key, type = 'text', extra = {}) => (
    <div>
      <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blush-200 ${errors[key] ? 'border-red-300 bg-red-50' : 'border-sage-200'}`}
        {...extra}
      />
      {errors[key] && <p className="text-red-500 text-xs mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {field('Company Name', 'name', 'text', { autoFocus: true, placeholder: 'Acme Construction' })}
      <div>
        <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1">URL Slug</label>
        <div className="flex items-center gap-2">
          <span className="text-sage-400 text-sm whitespace-nowrap">/c/</span>
          <input
            type="text"
            value={form.slug}
            onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            className={`flex-1 border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blush-200 ${errors.slug ? 'border-red-300 bg-red-50' : 'border-sage-200'}`}
            placeholder="acme-construction"
          />
        </div>
        {errors.slug && <p className="text-red-500 text-xs mt-1">{errors.slug}</p>}
        <p className="text-xs text-sage-400 mt-1">Coordinator bookmarks: order.phoenixkabob.com/#/c/{form.slug || '…'}</p>
      </div>
      {field('Delivery Address', 'delivery_address', 'text', { placeholder: '123 Industrial Blvd' })}
      <div className="grid grid-cols-2 gap-3">
        {field('City', 'delivery_city', 'text', { placeholder: 'Surprise' })}
        {field('ZIP', 'delivery_zip', 'text', { placeholder: '85374' })}
      </div>
      <div>
        <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1">Min. Order Bowls</label>
        <input
          type="number"
          min={1}
          value={form.min_order_bowls}
          onChange={e => set('min_order_bowls', e.target.value)}
          className="w-24 border border-sage-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blush-200"
        />
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Saving…</> : <>{client ? 'Update Client' : 'Create Client'}</>}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
      </div>
    </form>
  );
}

// ── Coordinator invite form ───────────────────────────────────────────────────
function CoordinatorForm({ client, onInvited, onCancel }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError('Name and email are required'); return; }
    setLoading(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('create-corporate-user', {
        body: { name, email, corporate_client_id: client.id, slug: client.slug, company_name: client.name }
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setSuccess(true);
      if (onInvited) onInvited();
    } catch (err) {
      setError(err.message || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-50 border border-green-100 rounded-2xl p-5 space-y-2">
        <div className="flex items-center gap-3">
          <SafeIcon name="CheckCircle" className="text-green-500 text-xl" />
          <p className="font-bold text-green-700">Invite sent to {email}</p>
        </div>
        <p className="text-sm text-green-600 pl-8">They'll receive an email to set their password and land on /c/{client.slug}</p>
        <button onClick={onCancel} className="btn-secondary text-sm mt-2">Done</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 bg-sage-50/50 rounded-2xl p-5 border border-sage-100">
      <h4 className="font-bold text-charcoal text-sm">Invite Coordinator for {client.name}</h4>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1">Full Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full border border-sage-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blush-200"
            placeholder="Jane Smith"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full border border-sage-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blush-200"
            placeholder="jane@acmecorp.com"
          />
        </div>
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={loading} className="btn-primary text-sm flex items-center gap-2">
          {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Sending…</> : 'Send Invite Email'}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">Cancel</button>
      </div>
    </form>
  );
}

// ── Group orders list for a client ────────────────────────────────────────────
function ClientGroupOrders({ client }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const { data } = await supabase
        .from('group_orders')
        .select('*, group_order_items(id, recipient_name, payment_status)')
        .eq('corporate_client_id', client.id)
        .order('created_at', { ascending: false })
        .limit(10);
      setOrders(data || []);
      setLoading(false);
    }
    fetch();
  }, [client.id]);

  const STATUS_COLOR = {
    awaiting_payment: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    preparing: 'bg-blue-100 text-blue-700',
    out_for_delivery: 'bg-purple-100 text-purple-700',
    delivered: 'bg-sage-100 text-sage-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  if (loading) return <div className="text-sage-400 text-sm py-2">Loading orders…</div>;
  if (!orders.length) return <p className="text-sage-400 text-sm py-2">No group orders yet.</p>;

  return (
    <div className="space-y-2 mt-3">
      {orders.map(order => {
        const totalItems = order.group_order_items?.length || 0;
        const paidItems = order.group_order_items?.filter(i => i.payment_status === 'paid').length || 0;
        return (
          <div key={order.id} className="bg-white border border-sage-100 rounded-xl px-4 py-3 flex items-center justify-between gap-4 text-sm">
            <div>
              <p className="font-bold text-charcoal">{order.delivery_date} — {order.payment_mode === 'split' ? `${paidItems}/${totalItems} paid` : '1 card'}</p>
              <p className="text-sage-400 text-xs">#{order.id.substring(0, 8).toUpperCase()}</p>
            </div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_COLOR[order.status] || 'bg-sage-100 text-sage-700'}`}>
              {order.status.replace(/_/g, ' ')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function CorporateClientsPanel() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [inviting, setInviting] = useState(null);

  useEffect(() => { fetchClients(); }, []);

  async function fetchClients() {
    setFetchError('');
    const { data, error } = await supabase
      .from('corporate_clients')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('fetchClients error:', error);
      setFetchError(error.message);
    }
    setClients(data || []);
    setLoading(false);
  }

  async function handleSave(payload) {
    setSaving(true);
    setSaveError('');
    let error;
    if (editingClient) {
      ({ error } = await supabase.from('corporate_clients').update(payload).eq('id', editingClient.id));
    } else {
      ({ error } = await supabase.from('corporate_clients').insert(payload));
    }
    setSaving(false);
    if (error) {
      console.error('Save error:', error);
      setSaveError(error.message || 'Failed to save. Check the console for details.');
      return;
    }
    await fetchClients();
    setShowForm(false);
    setEditingClient(null);
  }

  async function toggleActive(client) {
    await supabase.from('corporate_clients').update({ active: !client.active }).eq('id', client.id);
    setClients(prev => prev.map(c => c.id === client.id ? { ...c, active: !c.active } : c));
  }

  async function handleDelete(client) {
    if (!window.confirm(`Delete "${client.name}"? This cannot be undone.`)) return;
    await supabase.from('corporate_clients').delete().eq('id', client.id);
    setClients(prev => prev.filter(c => c.id !== client.id));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-charcoal">Corporate Clients</h2>
          <p className="text-sage-500 text-sm mt-1">{clients.length} {clients.length === 1 ? 'company' : 'companies'} registered</p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setEditingClient(null); }}
            className="btn-primary flex items-center gap-2"
          >
            <SafeIcon name="Plus" />
            Add Company
          </button>
        )}
      </div>

      {/* Fetch error */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-600">
          <strong>Could not load clients:</strong> {fetchError}
          {fetchError.includes('does not exist') && (
            <p className="mt-1 text-red-500">The database migration may not have been applied yet. Run the SQL in <code>src/supabase/migrations/1810000000000-corporate-clients.sql</code> in your Supabase SQL editor.</p>
          )}
        </div>
      )}

      {/* Save error */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-600">
          <strong>Save failed:</strong> {saveError}
          {saveError.includes('does not exist') && (
            <p className="mt-1 text-red-500">Run the migration file <code>1810000000000-corporate-clients.sql</code> in your Supabase SQL editor first.</p>
          )}
        </div>
      )}

      {/* Create / edit form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="glass-panel p-6 space-y-4"
          >
            <h3 className="font-serif text-lg text-charcoal">{editingClient ? `Edit ${editingClient.name}` : 'New Corporate Client'}</h3>
            <ClientForm
              client={editingClient}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setSaveError(''); setEditingClient(null); }}
              saving={saving}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Client list */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blush-300" /></div>
      ) : clients.length === 0 ? (
        <div className="text-center py-16 text-sage-400">
          <SafeIcon name="Building2" className="text-4xl mx-auto mb-3 opacity-40" />
          <p className="font-medium">No corporate clients yet</p>
          <p className="text-sm mt-1">Add your first company to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {clients.map(client => (
            <div key={client.id} className="glass-panel overflow-hidden">
              {/* Client header row */}
              <div className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-blush-50 border border-blush-100 flex items-center justify-center shrink-0">
                  <SafeIcon name="Building2" className="text-blush-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-charcoal">{client.name}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${client.active ? 'bg-green-100 text-green-700' : 'bg-sage-100 text-sage-500'}`}>
                      {client.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-sage-500 truncate">/c/{client.slug} · {client.delivery_address}, {client.delivery_city}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setInviting(inviting?.id === client.id ? null : client)}
                    title="Invite coordinator"
                    className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-blush-50 text-sage-400 hover:text-blush-400 transition-colors"
                  >
                    <SafeIcon name="UserPlus" />
                  </button>
                  <button
                    onClick={() => { setEditingClient(client); setShowForm(true); }}
                    title="Edit"
                    className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-sage-50 text-sage-400 hover:text-charcoal transition-colors"
                  >
                    <SafeIcon name="Pencil" />
                  </button>
                  <button
                    onClick={() => toggleActive(client)}
                    title={client.active ? 'Deactivate' : 'Activate'}
                    className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-sage-50 text-sage-400 hover:text-charcoal transition-colors"
                  >
                    <SafeIcon name={client.active ? 'EyeOff' : 'Eye'} />
                  </button>
                  <button
                    onClick={() => setExpanded(expanded === client.id ? null : client.id)}
                    title="View orders"
                    className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-sage-50 text-sage-400 hover:text-charcoal transition-colors"
                  >
                    <SafeIcon name={expanded === client.id ? 'ChevronUp' : 'ChevronDown'} />
                  </button>
                  <button
                    onClick={() => handleDelete(client)}
                    title="Delete"
                    className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-red-50 text-sage-400 hover:text-red-400 transition-colors"
                  >
                    <SafeIcon name="Trash2" />
                  </button>
                </div>
              </div>

              {/* Invite coordinator form */}
              <AnimatePresence>
                {inviting?.id === client.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    className="px-5 pb-5"
                  >
                    <CoordinatorForm
                      client={client}
                      onInvited={() => {}}
                      onCancel={() => setInviting(null)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Group orders */}
              <AnimatePresence>
                {expanded === client.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    className="px-5 pb-5 border-t border-sage-100"
                  >
                    <p className="text-xs font-bold text-sage-400 uppercase tracking-wider pt-4 mb-1">Recent Group Orders</p>
                    <ClientGroupOrders client={client} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
